import React, { useEffect, useState } from 'react';
import { FurnitureShape as IFurnitureShape } from '../../../../types';
import { DEFAULT_FURNITURE_FILL_COLOR } from '../../defines';
import { useShape } from '../ShapeContext/useShape';

// Cache for storing raw SVG text to avoid re-fetching
const svgCache = new Map<string, string>();

// Function to extract SVG dimensions
const getSVGDimensions = (svgContent: string): { width: number; height: number } => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const svgElement = doc.querySelector('svg');
  
  if (svgElement) {
    const width = svgElement.getAttribute('width');
    const height = svgElement.getAttribute('height');
    const viewBox = svgElement.getAttribute('viewBox');
    
    if (width && height) {
      return {
        width: parseFloat(width),
        height: parseFloat(height)
      };
    } else if (viewBox) {
      const [, , vbWidth, vbHeight] = viewBox.split(' ').map(Number);
      return {
        width: vbWidth,
        height: vbHeight
      };
    }
  }
  
  // Default fallback
  return { width: 64, height: 64 };
};

export const FurnitureShapeSVG: React.FC = () => {
  const { shape } = useShape();
  const furnitureShape = shape as IFurnitureShape;
  const [svgContent, setSvgContent] = useState<string>('');
  const [svgDimensions, setSvgDimensions] = useState({ width: 64, height: 64 });
  const fillColor = shape.color || DEFAULT_FURNITURE_FILL_COLOR;
  const imgUrl = furnitureShape?.furniture?.url;

  useEffect(() => {
    if (!imgUrl) return;

    if (svgCache.has(imgUrl)) {
      // Use cached SVG text
      const cachedSvg = svgCache.get(imgUrl)!;
      const coloredSvgText = cachedSvg.replace(/currentColor/g, fillColor);
      setSvgContent(coloredSvgText);
      setSvgDimensions(getSVGDimensions(cachedSvg));
    } else {
      // Fetch and store the raw SVG text
      fetch(imgUrl)
        .then((response) => response.text())
        .then((svgText) => {
          svgCache.set(imgUrl, svgText); // Cache the raw SVG text
          const coloredSvgText = svgText.replace(/currentColor/g, fillColor);
          setSvgContent(coloredSvgText);
          setSvgDimensions(getSVGDimensions(svgText));
        })
        .catch((error) => console.error('Error loading SVG:', error));
    }
  }, [imgUrl, fillColor]); // Update when URL or color changes

  // Calculate the transform for the entire furniture group
  const transform = `translate(${shape.x}, ${shape.y})${shape.rotation ? ` rotate(${shape.rotation})` : ''}`;

  // Calculate the scale factors based on the actual SVG dimensions and desired shape size
  const scaleX = shape.width / svgDimensions.width;
  const scaleY = shape.height / svgDimensions.height;

  // Calculate text position and alignment
  const textX = (() => {
    if (furnitureShape.textAlign === 'center') {
      return shape.width / 2;
    } else if (furnitureShape.textAlign === 'right') {
      return shape.width;
    } else {
      return 0;
    }
  })();

  const textAnchor = furnitureShape.textAlign === 'center' ? 'middle' : furnitureShape.textAlign === 'right' ? 'end' : 'start';

  return (
    <g transform={transform}>
      {/* Render the furniture SVG */}
      <g
        dangerouslySetInnerHTML={{ __html: svgContent }}
        transform={`scale(${scaleX}, ${scaleY})`}
        opacity={shape.locked ? 0.5 : 1}
      />
      
      {/* Render the text if present */}
      {furnitureShape.text && (
        <text
          x={textX}
          y={furnitureShape.height + 10}
          fontSize={furnitureShape.fontSize || 16}
          fontFamily={furnitureShape.fontFamily || 'Arial'}
          fontWeight={furnitureShape.isBold ? 'bold' : 'normal'}
          fontStyle={furnitureShape.isItalic ? 'italic' : 'normal'}
          textDecoration={furnitureShape.isUnderline ? 'underline' : 'none'}
          fill={furnitureShape.textColor || '#000000'}
          textAnchor={textAnchor}
          dominantBaseline="hanging"
        >
          {furnitureShape.text}
        </text>
      )}
    </g>
  );
}; 